import React, { useState, useRef, useEffect } from 'react';
import { request } from '../../api/authenticationService';

const UploadExcel = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(''); // success | error | loading | ''
  const [message, setMessage] = useState('');
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
    setUploadStatus('');
    setMessage('');
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadStatus('error');
      setMessage('⚠️ Please select a file before uploading.');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      setUploadStatus('loading');
      setMessage('Uploading file...');

      const response = await request(
        'POST',
        '/superadmin/uploadxls',
        formData,
        'multipart/form-data',
      );

      setUploadStatus('success');
      setMessage('✅ File uploaded successfully!');
      console.log('File uploaded successfully:', response.data);
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadStatus('error');
      setMessage('❌ Error uploading file. Please try again.');
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setUploadStatus('');
    setMessage('');

    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  useEffect(() => {
    if (message && uploadStatus !== 'loading') {
      const timer = setTimeout(() => {
        setMessage('');
        setUploadStatus('');
      }, 5000); // 5 seconds
      return () => clearTimeout(timer);
    }
  }, [message, uploadStatus]);

  const getStatusClasses = () => {
    switch (uploadStatus) {
      case 'success':
        return 'bg-green-100 text-green-700 border-green-400';
      case 'error':
        return 'bg-red-100 text-red-700 border-red-400';
      case 'loading':
        return 'bg-gray-100 text-gray-600 border-gray-300';
      default:
        return 'hidden';
    }
  };

  return (
    <div className="container mx-auto text-center py-6">
      <h1 className="md:text-2xl text-2xl text-center py-2 font-semibold text-cyan-900">
        Add Multiple Students to a Teacher Using Excel Upload
        <br />
        <span dir="rtl" className="text-base text-gray-600">
          إضافة عدة طلاب إلى معلم واحد باستخدام رفع ملف إكسل
        </span>
      </h1>

      {/* Step-by-step instructions */}
      <div className="bg-zinc-50 border border-cyan-500 rounded-lg shadow-sm max-w-5xl mx-auto mt-4 text-left p-6">
        <h2 className="text-lg font-semibold text-cyan-700 mb-3">
          Steps to Upload Students List:
          <br />
          <span className="text-sm text-gray-600">
            خطوات رفع قائمة الطلاب
          </span>
        </h2>

        <ol className="list-decimal list-inside space-y-3 text-gray-800">
          <li>
            <span className="font-medium">Add a new teacher</span> —
            create a teacher account in the system.
            <br />
            <span className="text-sm text-gray-600">
              أضف معلماً جديداً — قم بإنشاء حساب معلم في النظام
            </span>
          </li>

          <li>
            <span className="font-medium">Show all teachers</span> —
            open the list of teachers to find the assigned ID.
            <br />
            <span className="text-sm text-gray-600">
              عرض جميع المعلمين — افتح قائمة المعلمين للعثور على رقم
              التعريف الخاص بهم
            </span>
          </li>

          <li>
            <span className="font-medium">
              Copy the <strong>Teacher ID</strong>
            </span>{' '}
            and paste it into your Excel template under the
            <code className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded mx-1">
              teacherId
            </code>{' '}
            column.
            <br />
            <span className="text-sm text-gray-600">
              انسخ رقم تعريف المعلم والصقه ضمن عمود
              <span dir="ltr" className="mx-1 font-mono inline-block">
                teacherId
              </span>
              في قالب الإكسل
            </span>
            <br />
            <div className="mt-3">
              <span className="font-medium">
                Example of Excel row:
              </span>
              <br />
              <span className="text-sm text-gray-600">
                مثال على سطر في ملف الإكسل
              </span>
            </div>
            <div className="overflow-x-auto mt-3">
              <table className="min-w-full border border-gray-300 text-sm bg-white rounded-md shadow-sm">
                <thead className="bg-gray-100 text-gray-800">
                  <tr>
                    <th className="px-3 py-2 border">firstName</th>
                    <th className="px-3 py-2 border">lastName</th>
                    <th className="px-3 py-2 border">
                      firstNameArabic
                    </th>
                    <th className="px-3 py-2 border">
                      lastNameArabic
                    </th>
                    <th className="px-3 py-2 border">email</th>
                    <th className="px-3 py-2 border">teacherId</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="text-center">
                    <td className="px-3 py-2 border">Mark</td>
                    <td className="px-3 py-2 border">Zucker</td>
                    <td className="px-3 py-2 border">مارك</td>
                    <td className="px-3 py-2 border">سكر</td>
                    <td className="px-3 py-2 border">
                      example@gmail.com
                    </td>
                    <td className="px-3 py-2 border text-left font-semibold text-blue-700">
                      25
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </li>

          <li>
            <span className="font-medium">Upload the Excel file</span>
            <br />
            <span className="text-sm text-gray-600">
              قم برفع ملف الإكسل.
            </span>
          </li>
        </ol>
      </div>

      <div className="flex flex-col sm:flex-row justify-center items-center gap-3 mt-4">
        <input
          ref={fileInputRef}
          type="file"
          accept=".xls,.xlsx"
          onChange={handleFileChange}
          className="block text-sm text-gray-700 border border-gray-300 rounded-md shadow-sm bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
        />

        <button
          onClick={handleUpload}
          disabled={uploadStatus === 'loading'}
          className={`rounded px-6 py-2 font-semibold text-white transition ${
            uploadStatus === 'loading'
              ? 'bg-gray-500 cursor-not-allowed'
              : 'bg-green-700 hover:bg-green-800'
          }`}
        >
          {uploadStatus === 'loading' ? 'Uploading...' : 'Upload'}
        </button>

        <button
          onClick={handleReset}
          className="rounded bg-gray-600 text-white px-6 py-2 font-semibold hover:bg-gray-700 transition"
        >
          Reset
        </button>
      </div>

      {/* Status indicator */}
      <div
        className={`mt-4 mx-auto border rounded-md px-4 py-3 text-sm font-medium w-fit ${getStatusClasses()}`}
      >
        {message}
      </div>

      <hr className="w-960 h-2 mx-auto my-6 bg-cyan-800 border-0 rounded" />
    </div>
  );
};

export default UploadExcel;
